import { useEffect, useRef, useState } from "react";

export function useActiveMotion<T extends HTMLElement>(threshold = 0.18) {
  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [isDocumentVisible, setIsDocumentVisible] = useState(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncPreference = () => setPrefersReducedMotion(media.matches);
    const syncVisibility = () => setIsDocumentVisible(!document.hidden);
    const node = ref.current;

    syncPreference();
    syncVisibility();

    const observer = node
      ? new IntersectionObserver(
          ([entry]) => setIsVisible(entry.isIntersecting),
          { rootMargin: "140px 0px", threshold }
        )
      : null;

    if (node) observer?.observe(node);
    media.addEventListener("change", syncPreference);
    document.addEventListener("visibilitychange", syncVisibility);

    return () => {
      observer?.disconnect();
      media.removeEventListener("change", syncPreference);
      document.removeEventListener("visibilitychange", syncVisibility);
    };
  }, [threshold]);

  return {
    ref,
    prefersReducedMotion,
    motionActive: isVisible && isDocumentVisible && !prefersReducedMotion
  };
}
